import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixEcomV1EcommerceSettings from './ecom-v1-ecommerce-settings-ecommerce-settings.http.js';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';

export interface EcommerceSettings {
  /**
   * Additional Fees service plugin configuration.
   * @internal
   */
  additionalFeesServicePlugin?: ServicePluginConfiguration;
  /**
   * Validations service plugin configuration.
   * @internal
   */
  validationsServicePlugin?: ServicePluginConfiguration;
  /**
   * Tax configuration.
   * @internal
   */
  tax?: TaxConfiguration;
  /**
   * Discount rules configuration.
   * @internal
   */
  discountRules?: DiscountRulesConfiguration;
  /**
   * Checkout settings.
   * @internal
   */
  checkout?: CheckoutSettings;
  /**
   * Delivery configuration.
   * @internal
   */
  delivery?: DeliveryConfiguration;
  /**
   * Whether the site is a premium site.
   * @internal
   */
  premiumSite?: boolean | null;
  /**
   * Business information.
   * @internal
   */
  businessInfo?: BusinessProperties;
}

export interface ServicePluginConfiguration {
  /**
   * List of active providers.
   * @maxSize 100
   */
  activeProviders?: ActiveProvider[];
}

export interface ActiveProvider {
  /**
   * App ID of the provider.
   * @format GUID
   */
  appId?: string;
  /**
   * Flows that trigger the provider.
   * @maxSize 100
   */
  triggerFlows?: TriggerFlowWithLiterals[];
}

export enum TriggerFlow {
  /** Provider is triggered during cart interactions. */
  CART = 'CART',
  /** Provider is triggered during the checkout process. */
  CHECKOUT = 'CHECKOUT',
}

/** @enumType */
export type TriggerFlowWithLiterals = TriggerFlow | 'CART' | 'CHECKOUT';

export interface TaxConfiguration {
  /**
   * Whether at least one tax region is configured and connected to a tax calculator.
   * Learn more about the [Tax Regions API](https://dev.wix.com/docs/rest/business-solutions/e-commerce/tax/tax-regions/introduction).
   */
  configured?: boolean | null;
  /**
   * Whether tax is included in item prices.
   * Learn more about [tax inclusion in item prices](https://support.wix.com/en/article/including-tax-in-the-product-price).
   */
  taxIncludedInItemPrices?: boolean | null;
}

export interface DiscountRulesConfiguration {
  /**
   * Whether at least one discount rule is configured and active.
   * Learn more about the [Discount Rules API](https://dev.wix.com/docs/rest/business-solutions/e-commerce/discount-rules/introduction).
   */
  active?: boolean | null;
}

/** Site-level settings that control the checkout experience, including policies, field visibility, and branding. */
export interface CheckoutSettings {
  /** Checkout policies. */
  checkoutPolicies?: CheckoutPolicies;
  /** Settings that apply to checkout fields and the checkout process. */
  checkoutFields?: CheckoutFields;
  /** Checkout brand settings. */
  checkoutBrand?: CheckoutBrand;
  /**
   * Date and time the checkout settings were created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the checkout settings were updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Billing information required to complete the checkout. */
  checkoutBillingFields?: CheckoutBillingFieldsWithLiterals;
}

export interface CheckoutPolicies {
  /** Terms and conditions. */
  termsAndConditions?: TermsAndConditionsPolicy;
  /** Privacy policy. */
  privacyPolicy?: PrivacyPolicy;
  /** Return policy. */
  returnPolicy?: ReturnPolicy;
  /** Digital item policy. */
  digitalItemPolicy?: DigitalItemPolicy;
  /** "Contact us" information. */
  contactUs?: ContactUsPolicy;
  /** Custom policy. */
  customPolicy?: CustomCheckoutPolicy;
}

export interface TermsAndConditionsPolicy {
  /**
   * Whether the terms and conditions policy is visible to the customer in the checkout page. <br><br>
   *
   * Default: `false`
   */
  visible?: boolean | null;
  /** Terms and conditions policy content. */
  content?: string | null;
}

export interface PrivacyPolicy {
  /**
   * Whether the privacy policy is visible to the customer in the checkout page. <br><br>
   *
   * Default: `false`
   */
  visible?: boolean | null;
  /** Privacy policy content. */
  content?: string | null;
}

export interface ReturnPolicy {
  /**
   * Whether the return policy is visible to the customer in the checkout page. <br><br>
   *
   * Default: `false`
   */
  visible?: boolean | null;
  /** Return policy content. */
  content?: string | null;
}

export interface DigitalItemPolicy {
  /**
   * Whether the digital item policy is visible to the customer in the checkout page. <br><br>
   *
   * Default: `false`
   */
  visible?: boolean | null;
  /** Digital item policy content. */
  content?: string | null;
}

export interface ContactUsPolicy {
  /**
   * Whether the contact us policy is visible to the customer in the checkout page. <br><br>
   *
   * Default: `false`
   */
  visible?: boolean | null;
  /** Contact us policy content. */
  content?: string | null;
}

export interface CustomCheckoutPolicy {
  /**
   * Whether the custom policy is visible to the customer in the checkout page. <br><br>
   *
   * Default: `false`
   */
  visible?: boolean | null;
  /** Custom policy content. */
  content?: string | null;
  /** Custom policy title. */
  title?: string | null;
}

export interface CheckoutFields {
  /**
   * Subscription checkbox. <br><br>
   *
   * Defaults:
   * + `visible`: `false`
   * + `checkedByDefault`: `false`
   */
  subscriptionCheckbox?: CheckboxField;
  /**
   * Policy agreement checkbox. <br><br>
   *
   * Defaults:
   * + `visible`: `false`
   * + `checkedByDefault`: `false`
   */
  policyAgreementCheckbox?: CheckboxField;
  /**
   * Whether the ability to redeem a gift card is enabled. <br><br>
   *
   * Default: `false`
   */
  giftCardRedeemEnabled?: boolean | null;
  /**
   * Whether to allow for MIT transactions. <br><br>
   *
   * Default: `false`
   */
  mitEnabled?: boolean | null;
  /**
   * Whether to allow for Auth & Capture transactions. <br><br>
   *
   * Default: `false`
   */
  delayCaptureEnabled?: boolean | null;
  /**
   * Whether to automatically capture the transaction before the authorization expires.
   *
   * Default: `true`
   * >**Note:** this setting is only relevant when `delay_capture_enabled: true`.
   */
  captureBeforeExpiryEnabled?: boolean | null;
}

export interface CheckboxField {
  /** Whether the checkbox is visible to the customer. */
  visible?: boolean | null;
  /** Whether the checkbox is checked by default. */
  checkedByDefault?: boolean | null;
}

export interface CheckoutBrand {
  /** Checkout header. */
  header?: CheckoutHeader;
  /**
   * Main checkout area branding.
   * @internal
   */
  mainSection?: CheckoutMain;
  /**
   * Checkout footer branding.
   * @internal
   */
  footer?: CheckoutFooter;
  /**
   * Global checkout branding.
   * @internal
   */
  global?: CheckoutGlobal;
}

export interface CheckoutHeader {
  /**
   * Textual content to be included in the header (e.g. Business Name)
   * @maxLength 55
   */
  text?: string | null;
  /**
   * Text color hex code.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /** Specifications for when a logo is included in the header. */
  logo?: Logo;
  /**
   * Alignment within the header.
   *
   * >**Note:** `SIDE` alignment is decided depending on the locale's language. For example, scripts like Hebrew and Arabic will automatically align right-to-left.
   */
  alignment?: AlignmentWithLiterals;
  /**
   * Whether to display the 'Checkout' label within the header.
   * Depending on the locale, the label may be translated.
   */
  displayCheckoutLabel?: boolean | null;
  /** Continue shopping specifications. */
  continueShopping?: ContinueShopping;
  /** Background fill specifications. */
  backgroundFill?: BackgroundFill;
  /**
   * Color hex code of the full-width divider rule below the header.
   * @internal
   * @format COLOR_HEX
   */
  dividerColor?: string | null;
}

export interface Logo {
  /** Size of the logo. */
  size?: LogoSizeWithLiterals;
}

export interface FocalPoint {
  /** X-coordinate of the focal point. */
  x?: number;
  /** Y-coordinate of the focal point. */
  y?: number;
  /** crop by height */
  height?: number | null;
  /** crop by width */
  width?: number | null;
}

export enum LogoSize {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

/** @enumType */
export type LogoSizeWithLiterals = LogoSize | 'SMALL' | 'MEDIUM' | 'LARGE';

export enum Alignment {
  SIDE = 'SIDE',
  CENTER = 'CENTER',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'SIDE' | 'CENTER';

export interface ContinueShopping {
  /** Whether the continue shopping element is hidden. */
  hidden?: boolean | null;
  /** Display type for the continue shopping element. */
  displayType?: DisplayTypeWithLiterals;
}

export enum DisplayType {
  TEXT = 'TEXT',
  ICON = 'ICON',
}

/** @enumType */
export type DisplayTypeWithLiterals = DisplayType | 'TEXT' | 'ICON';

export interface BackgroundFill extends BackgroundFillOptionsOneOf {
  /**
   * Background color hex code.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Background image. */
  image?: string;
}

/** @oneof */
export interface BackgroundFillOptionsOneOf {
  /**
   * Background color hex code.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Background image. */
  image?: string;
}

export interface CheckoutMain {
  /**
   * Background fill of the checkout form.
   * @internal
   */
  formBackgroundFill?: BackgroundFill;
  /**
   * Background fill of the checkout summary.
   * @internal
   */
  summaryBackgroundFill?: BackgroundFill;
  /**
   * Color hex code of the dividers.
   * @internal
   * @format COLOR_HEX
   */
  dividerColor?: string | null;
  /**
   * Color hex code of the tinted boxes (e.g. show-summary box). Stored and applied solid (no opacity).
   * @internal
   * @format COLOR_HEX
   */
  tintedBoxColor?: string | null;
  /**
   * Corner radius style applied to the express checkout buttons.
   * @internal
   */
  expressButtonsCornerRadius?: ExpressButtonsCornerRadiusWithLiterals;
  /**
   * Corner radius applied everywhere except the express checkout buttons — line-item images and form-field inputs.
   * @internal
   * @max 100
   */
  cornerRadius?: number | null;
}

export enum ExpressButtonsCornerRadius {
  RECTANGLE = 'RECTANGLE',
  PILL = 'PILL',
}

/** @enumType */
export type ExpressButtonsCornerRadiusWithLiterals =
  | ExpressButtonsCornerRadius
  | 'RECTANGLE'
  | 'PILL';

export interface CheckoutFooter {
  /**
   * Background fill of the footer.
   * @internal
   */
  backgroundFill?: BackgroundFill;
  /**
   * Color hex code of the full-width divider rule above the footer.
   * @internal
   * @format COLOR_HEX
   */
  dividerColor?: string | null;
}

export interface CheckoutGlobal {
  /**
   * Color hex code of selection controls (radios/checkboxes).
   * @internal
   * @format COLOR_HEX
   */
  selectionColor?: string | null;
  /**
   * Color hex code of the place order button.
   * @internal
   * @format COLOR_HEX
   */
  placeOrderButtonColor?: string | null;
  /**
   * Color hex code of the buttons.
   * @internal
   * @format COLOR_HEX
   */
  buttonColor?: string | null;
  /**
   * Background style applied to checkout input fields.
   * @internal
   */
  inputFieldBackgroundStyle?: InputFieldBackgroundStyleWithLiterals;
  /**
   * Font family used for headings.
   * @internal
   * @maxLength 30
   */
  headingsFontFamily?: string | null;
  /**
   * Font family used for body text.
   * @internal
   * @maxLength 30
   */
  bodyFontFamily?: string | null;
}

export enum InputFieldBackgroundStyle {
  WHITE = 'WHITE',
  TRANSPARENT = 'TRANSPARENT',
}

/** @enumType */
export type InputFieldBackgroundStyleWithLiterals =
  | InputFieldBackgroundStyle
  | 'WHITE'
  | 'TRANSPARENT';

export enum CheckoutBillingFields {
  /** All billing information fields are required. */
  All_FIELDS = 'All_FIELDS',
  /** Minimum required fields as defined by the country, payment method and payment provider. */
  MINIMUM_REQUIRED_FIELDS = 'MINIMUM_REQUIRED_FIELDS',
}

/** @enumType */
export type CheckoutBillingFieldsWithLiterals =
  | CheckoutBillingFields
  | 'All_FIELDS'
  | 'MINIMUM_REQUIRED_FIELDS';

export interface DeliveryConfiguration {
  /**
   * Scope of destinations, when `WORLDWIDE` is selected, the destinations list will be empty.
   * @deprecated Scope of destinations, when `WORLDWIDE` is selected, the destinations list will be empty.
   * @targetRemovalDate 2025-12-31
   */
  destinationScope?: DestinationScopeWithLiterals;
  /**
   * The list of destinations.
   * @maxSize 200
   * @deprecated The list of destinations.
   * @targetRemovalDate 2025-12-31
   */
  destinations?: Destination[];
  /**
   * Whether postal code is required to support delivery calculations.
   * @deprecated Whether postal code is required to support delivery calculations.
   * @targetRemovalDate 2025-12-31
   */
  postalCodeRequired?: boolean | null;
  /**
   * List of countries and subdivisions and their delivery destination properties
   * @maxSize 195
   */
  countries?: CountryDeliveryProperties[];
  /** Delivery destination properties for Rest Of World */
  restOfWorld?: DeliveryDestinationProperties;
}

export enum DestinationScope {
  /** Specific destinations are configured. */
  SPECIFIC_DESTINATIONS = 'SPECIFIC_DESTINATIONS',
  /** All destinations worldwide. */
  WORLDWIDE = 'WORLDWIDE',
}

/** @enumType */
export type DestinationScopeWithLiterals =
  | DestinationScope
  | 'SPECIFIC_DESTINATIONS'
  | 'WORLDWIDE';

/** Geographic destination for a delivery region. */
export interface Destination {
  /**
   * 2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  countryCode?: string;
  /**
   * Subdivision codes in [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format. For example, `"US-CA"` for California. If empty, the delivery region applies to the entire country.
   *
   * The following countries don't support subdivision-level delivery and require an empty `subdivisions` list. Sending subdivisions for these countries fails with `SUBDIVISIONS_UNSUPPORTED_FOR_COUNTRY`:
   * `AT`, `BE`, `BS`, `CH`, `CI`, `CZ`, `DO`, `GR`, `NO`, `PL`, `RS`, `SD`, `SI`, `SK`, `SN`.
   * @maxSize 100
   * @minLength 1
   * @maxLength 256
   */
  subdivisions?: string[];
}

export interface CountryDeliveryProperties {
  /**
   * 2-letter country code in [ISO-3166 alpha-2](https://www.iso.org/obp/ui/#search/code/) format.
   * @format COUNTRY
   */
  country?: string;
  /** Delivery destination properties for the country. */
  deliveryDestinationProperties?: DeliveryDestinationProperties;
  /**
   * List of subdivisions and their delivery destination properties.
   * @maxSize 100
   */
  subdivisions?: SubdivisionDeliveryProperties[];
}

export interface DeliveryDestinationProperties {
  /** Whether a postal code is required for delivery to this destination. */
  postalCodeRequired?: boolean;
  /** Whether only pickup is available at this destination. */
  pickupOnly?: boolean;
}

export interface SubdivisionDeliveryProperties {
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://www.iso.org/standard/72483.html) format.
   * @minLength 1
   * @maxLength 5
   */
  subdivision?: string;
  /** Delivery destination properties for the subdivision. */
  deliveryDestinationProperties?: DeliveryDestinationProperties;
}

/** Ecommerce business properties, as defined in the source of truth: `site-properties`, including default values. */
export interface BusinessProperties {
  /**
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * Payment currency for the business (default is 'USD').
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   * Used by the business for site content, emails, notifications, etc (default is 'en').
   * @format LANGUAGE
   */
  language?: string;
  /** Locale for date, number, and measurement formatting (default is `en-US`). */
  locale?: Locale;
  /** Physical address of the business. */
  address?: Address;
  /** Business name. */
  name?: string | null;
  /** Business email address. */
  email?: string | null;
  /** Business phone number. */
  phone?: string | null;
  /** Business fax number. */
  fax?: string | null;
  /** Path to the business logo in Wix Media. */
  logo?: string | null;
  /** Business time zone. */
  timeZone?: string | null;
  /** Site display name. */
  siteDisplayName?: string | null;
}

/** Locale for date, number, and measurement formats. */
export interface Locale {
  /**
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   * @format LANGUAGE
   */
  language?: string;
  /**
   * Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   */
  country?: string | null;
}

/** Physical address */
export interface Address {
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision code in ISO 3166-2 format, e.g., "US-NY". */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Street name and number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number as free text. */
  addressLine1?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface GetEcommerceSettingsRequest {
  /**
   * Fields to include in the response.
   * @minSize 1
   * @maxSize 25
   */
  fields: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  ADDITIONAL_FEES_SERVICE_PLUGIN = 'ADDITIONAL_FEES_SERVICE_PLUGIN',
  VALIDATIONS_SERVICE_PLUGIN = 'VALIDATIONS_SERVICE_PLUGIN',
  TAX = 'TAX',
  DISCOUNT_RULES = 'DISCOUNT_RULES',
  CHECKOUT = 'CHECKOUT',
  DELIVERY = 'DELIVERY',
  PREMIUM_SITE = 'PREMIUM_SITE',
  BUSINESS_INFO = 'BUSINESS_INFO',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'ADDITIONAL_FEES_SERVICE_PLUGIN'
  | 'VALIDATIONS_SERVICE_PLUGIN'
  | 'TAX'
  | 'DISCOUNT_RULES'
  | 'CHECKOUT'
  | 'DELIVERY'
  | 'PREMIUM_SITE'
  | 'BUSINESS_INFO';

export interface GetEcommerceSettingsResponse {
  /** Ecommerce settings. */
  ecommerceSettings?: EcommerceSettings;
  /** A list of errors that occurred while retrieving the eCommerce settings. */
  errors?: Error[];
}

export interface Error {
  /** The requested field the error is related to. */
  requestedField?: RequestedFieldsWithLiterals;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface FeatureEvent extends FeatureEventEventOneOf {
  /**
   * Information about an event that makes a feature eligible to the user.
   * Triggered for example, for new features or when a feature is reassigned
   * to an account or a site.
   */
  enabled?: FeatureEnabled;
  /**
   * Information about an event that disables a feature for the user.
   * Triggered for example, when a feature is unassigned from a site,
   * reassigned to a different site, or the user switched to a different contract.
   */
  disabled?: FeatureDisabled;
  /**
   * Information about an event that updates a feature. An `updated` event
   * is triggered for example by the
   * [Report Quota Usage](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/report-quota-usage)
   * and [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter)
   * endpoints.
   */
  updated?: FeatureUpdated;
  /**
   * Information about an event that cancels a feature for the user.
   * Triggered for example, when a feature is canceled, transferred to
   * another account, or the user switched to a different contract.
   */
  cancelled?: FeatureCancelled;
  /**
   * Information about a quota usage change for a feature. Triggered when
   * quota usage is reported via Report Quota Usage endpoint. Includes
   * before/after usage and remaining values.
   */
  quotaUsageChanged?: FeatureQuotaUsageChanged;
  /**
   * Timestamp of the event in
   * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
   */
  timestamp?: Date | null;
}

/** @oneof */
export interface FeatureEventEventOneOf {
  /**
   * Information about an event that makes a feature eligible to the user.
   * Triggered for example, for new features or when a feature is reassigned
   * to an account or a site.
   */
  enabled?: FeatureEnabled;
  /**
   * Information about an event that disables a feature for the user.
   * Triggered for example, when a feature is unassigned from a site,
   * reassigned to a different site, or the user switched to a different contract.
   */
  disabled?: FeatureDisabled;
  /**
   * Information about an event that updates a feature. An `updated` event
   * is triggered for example by the
   * [Report Quota Usage](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/report-quota-usage)
   * and [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter)
   * endpoints.
   */
  updated?: FeatureUpdated;
  /**
   * Information about an event that cancels a feature for the user.
   * Triggered for example, when a feature is canceled, transferred to
   * another account, or the user switched to a different contract.
   */
  cancelled?: FeatureCancelled;
  /**
   * Information about a quota usage change for a feature. Triggered when
   * quota usage is reported via Report Quota Usage endpoint. Includes
   * before/after usage and remaining values.
   */
  quotaUsageChanged?: FeatureQuotaUsageChanged;
}

/** Feature created or enabled after disabled state */
export interface FeatureEnabled extends FeatureEnabledReasonOneOf {
  /** Information about a transfer from another account. */
  transferredFromAnotherAccount?: TransferredFromAnotherAccountReason;
  /** Information about a transfer from another site. */
  reassignedFromSite?: ReassignedFromSiteReason;
  /** Information about a feature that hadn't been assigned to site. */
  assignedFromFloating?: AssignedFromFloatingReason;
  /** Information about the new feature. */
  newFeature?: NewFeatureReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the manually created features. */
  manualFeatureCreation?: ManualFeatureCreationReason;
  /** Information about a feature that was migrated from legacy. */
  migratedFromLegacy?: MigratedFromLegacyReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /** Enabled feature. */
  feature?: Feature;
  /**
   * Information about a transfer from another account.
   * __Deprecated__. Use `reason.transferred_from_another_account` instead.
   * @format GUID
   */
  transferredFromAccount?: string | null;
  /**
   * Information about a transfer from another site.
   * __Deprecated__. Use `reason.reassigned_from_site` instead.
   * @format GUID
   */
  reassignedFromMetasite?: string | null;
}

/** @oneof */
export interface FeatureEnabledReasonOneOf {
  /** Information about a transfer from another account. */
  transferredFromAnotherAccount?: TransferredFromAnotherAccountReason;
  /** Information about a transfer from another site. */
  reassignedFromSite?: ReassignedFromSiteReason;
  /** Information about a feature that hadn't been assigned to site. */
  assignedFromFloating?: AssignedFromFloatingReason;
  /** Information about the new feature. */
  newFeature?: NewFeatureReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the manually created features. */
  manualFeatureCreation?: ManualFeatureCreationReason;
  /** Information about a feature that was migrated from legacy. */
  migratedFromLegacy?: MigratedFromLegacyReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
}

export interface Feature extends FeatureQuantityInfoOneOf {
  /**
   * Deprecated. Use `enabled` instead.
   * @deprecated
   */
  booleanFeature?: BooleanFeature;
  /**
   * Deprecated. Use `quotaInfo` instead.
   * @deprecated
   */
  quotaFeature?: QuotaFeature;
  /**
   * ID of the feature. __Note:__ Isn't unique. For example, all features that
   * are available to free Wix accounts or site in some capacity have
   * `{"id": "DEFAULT"}`. Use `uniqueName` as unique identifier for a feature.
   * @readonly
   */
  _id?: string;
  /**
   * Unique name of the feature. Only lower case letters, numbers, and dashes
   * `-` are supported. Used in the endpoints of the
   * [Features Manager API](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/introduction)
   * to specify the feature. Not visible to customers. We recommend to start
   * the unique name with a prefix describing your organization or Wix company.
   * For example, `bookings` or `crm`.
   *
   * Min: 2 characters
   * Max: 50 characters
   * @minLength 2
   * @maxLength 50
   */
  uniqueName?: string;
  /**
   * Information about whether the feature belongs to a Wix account or site.
   * Account features have `context.userId`. Site features have `context.metaSiteId` in case
   * they're assigned to a specific site. Site features that aren't assigned to
   * a specific site have neither ID.
   */
  context?: FeatureContext;
  /**
   * Deprecated.
   * @readonly
   * @deprecated
   */
  createdAt?: Date | null;
  /**
   * Deprecated.
   * @readonly
   * @deprecated
   */
  updatedAt?: Date | null;
  /**
   * Aggregated information about how often customers can use the feature.
   * Available only for quota features.
   */
  quotaInfo?: QuotaInfo;
  /**
   * Whether the customer is currently allowed to use the feature.
   * `true` means that the customer can use the feature. This means a boolean
   * feature is active or a quota feature has remaining usage.
   * `false` means that the customer can't use the feature.
   * This means a boolean feature isn't active or a quota feature doesn't
   * have remaining usage.
   */
  enabled?: boolean;
  /**
   * ID of the [subscription](https://bo.wix.com/wix-docs/rest/premium/premium-subscriptions-manager/subscription-object)
   * to which the feature instance belongs.
   * @format GUID
   */
  subscriptionId?: string | null;
  /**
   * Metadata of the feature. Wix Premium uses the metadata object to indicate
   * that customers who purchase a product with the feature also get
   * access to an additional product. For these bundled products `metadata`
   * looks like this: `{"tpa": "{"appDefId": "sample-app-def-id-1234567890", "vendorProductId": "sample-productId"}}"`.
   * But you can use the `metadata` property for other purposes, too.
   */
  metadata?: Record<string, string>;
}

/** @oneof */
export interface FeatureQuantityInfoOneOf {
  /**
   * Deprecated. Use `enabled` instead.
   * @deprecated
   */
  booleanFeature?: BooleanFeature;
  /**
   * Deprecated. Use `quotaInfo` instead.
   * @deprecated
   */
  quotaFeature?: QuotaFeature;
}

/**
 * Context this feature is currently connected to.
 * Note: Do not confuse with feature scope which is configured in the product catalog
 * and defines in which context the product can be used
 */
export interface FeatureContext {
  /**
   * ID of the Wix account that the feature instance belongs to.
   * Available for both site and account level feature instances.
   * @format GUID
   */
  userId?: string;
  /**
   * ID of the meta site that the feature instance is assigned to.
   * Only available for site level feature instances that are assigned to a Wix
   * site. Not available for account level and unassigned site level feature
   * instances.
   * @format GUID
   */
  metaSiteId?: string | null;
}

/**
 * A feature that can be either "enabled" or "disabled". The default/freemium setting is always OFF, and the premium setting is always ON (meaning, unlimited usage without tracking).
 * A boolean feature is similar to a quantitive feature with a default limit of 0 and UNLIMITED premium limit (although a bit simplified).
 */
export interface BooleanFeature {}

/** A feature with a periodic usage limitation. The default limit is defined in the Feature Spec, the Premium limits are defined in the respective ProductFeature. */
export interface QuotaFeature {
  /** Default (or Freemium) quota limitation. if left undefined the free feature has unlimited amount. */
  limit?: string | null;
  /** Periodic time-frame to reset the usage counter. You may use NO_PERIOD if counter shouldn't be reset. */
  period?: FeaturePeriodWithLiterals;
  /** Usage measurement units (seconds? MBs? unitless?). Usage reported will be counted in multiples of this basic unit. */
  units?: string | null;
}

/** Determines the reset cycle of the feature usage. */
export enum FeaturePeriod {
  NO_PERIOD = 'NO_PERIOD',
  MILLISECOND = 'MILLISECOND',
  SECOND = 'SECOND',
  MINUTE = 'MINUTE',
  HOUR = 'HOUR',
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  YEAR = 'YEAR',
}

/** @enumType */
export type FeaturePeriodWithLiterals =
  | FeaturePeriod
  | 'NO_PERIOD'
  | 'MILLISECOND'
  | 'SECOND'
  | 'MINUTE'
  | 'HOUR'
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR';

export interface QuotaInfo {
  /**
   * Aggregation of how often the customer is allowed to use the feature at the time of the request.
   * `null` means that the customer has unlimited access to the feature.
   */
  limit?: string | null;
  /**
   * Deprecated because a feature can have multiple periods defined. Use `period` in `breakdown` entries instead.
   * Time frame for the usage limitation. `NO_PERIOD` means that `remainingUsage`
   * isn't automatically reset to the feature's `limit` after a specific period.
   * You may still manually call
   * [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter).
   * @deprecated
   * @replacedBy breakdown.period
   * @targetRemovalDate 2026-03-31
   */
  period?: FeaturePeriodWithLiterals;
  /**
   * Aggregation of how often the customer has used the feature.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will contain the value `-1`.
   */
  currentUsage?: string;
  /**
   * Aggregation of how often the customer can still use the feature.
   * `null` means that the customer has unlimited access to the feature.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will not be populated.
   */
  remainingUsage?: string | null;
  /**
   * Breakdown of quota information entries, entry per feature instance.
   * @maxSize 1000
   */
  breakdown?: QuotaInfoEntry[];
}

export interface QuotaInfoEntry {
  /**
   * If quota is of free feature, the id would be empty (None).
   * If quota is of premium feature, this is the ID of the feature instance.
   * @format GUID
   */
  featureInstanceId?: string | null;
  /**
   * How often the customer is allowed to use the feature during the specified
   * period. `null` means that the customer has unlimited access to the feature.
   */
  limit?: string | null;
  /**
   * Time frame for the usage limitation. `NO_PERIOD` means that `remainingUsage`
   * isn't automatically reset to the feature's `limit` after a specific period.
   * You may still manually call
   * [Reset Usage Counter](https://bo.wix.com/wix-docs/rest/premium/premium-features-manager/reset-usage-counter).
   */
  period?: FeaturePeriodWithLiterals;
  /**
   * How often the customer has used the feature during the current
   * period.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will contain the value `-1`.
   */
  currentUsage?: string;
  /**
   * How often the customer can still use the feature during the current
   * period. `null` means that the customer has unlimited access to the feature.
   * This value is retrieved from the Quota Manager/Rate Limiter system. If the
   * `exclude_quota_usage` field in the `ListFeaturesRequest` was set to `true`,
   * this field will not be populated.
   */
  remainingUsage?: string | null;
  /**
   * ID of the subscription to which the feature instance belongs.
   * @format GUID
   */
  subscriptionId?: string | null;
  /**
   * Reset date of the quota.
   * Relevant only for features with a period that is not `NO_PERIOD`, and only for features that are managed by the Quota Manager system.
   */
  resetDate?: Date | null;
}

/** Subscription transferred from another account, features on the current account were enabled. */
export interface TransferredFromAnotherAccountReason {
  /**
   * Information about a transfer from another account.
   * @format GUID
   */
  transferredFromAccount?: string;
}

/** Subscription moved from one site to another in the same account, features enabled on the target site */
export interface ReassignedFromSiteReason {
  /**
   * Information about a transfer from another site.
   * @format GUID
   */
  reassignedFromMetasite?: string;
}

/** Subscription was floating and assigned to site, features enabled on the target site */
export interface AssignedFromFloatingReason {}

/** New subscription created and features created as enabled */
export interface NewFeatureReason {}

/** Subscription was upgraded or downgraded, as a result new features enabled, missing features disabled , quantities are updated */
export interface ContractSwitchedReason {
  /** Classification of the contract switch (upgrade, downgrade, or price increase) */
  classification?: ContractSwitchClassificationWithLiterals;
}

/**
 * Classification of contract switch events for quota reset behavior
 * Needed to determine whether to reset usage and/or reset date for features affected by the contract switch.
 * For example, for an upgrade we want to reset both usage and date.
 * For a downgrade we want to keep the usage and date but update the limit, and for a price increase we want to reset the usage but keep the date.
 */
export enum ContractSwitchClassification {
  /** Unknown or unclassified contract switch */
  UNKNOWN = 'UNKNOWN',
  /** Upgrade - reset usage, reset date, and update limit */
  UPGRADE = 'UPGRADE',
  /** Downgrade - only update limit (no reset) */
  DOWNGRADE = 'DOWNGRADE',
  /** Price increase - reset usage only (not reset date), update limit */
  PRICE_INCREASE = 'PRICE_INCREASE',
}

/** @enumType */
export type ContractSwitchClassificationWithLiterals =
  | ContractSwitchClassification
  | 'UNKNOWN'
  | 'UPGRADE'
  | 'DOWNGRADE'
  | 'PRICE_INCREASE';

/** a call to CreateFeature in features-writer, creates feature that is not attached to subscription */
export interface ManualFeatureCreationReason {}

/** Subscription created due to migration from old premium model */
export interface MigratedFromLegacyReason {}

/** Feature was synced manually, for example by immigrator task */
export interface ManualSyncReason {
  /**
   * Optional description of the manual sync source (e.g. "immigrator", "quota-sync").
   * @maxLength 100
   */
  source?: string | null;
}

/** Feature disabled and can be enabled in the future */
export interface FeatureDisabled extends FeatureDisabledReasonOneOf {
  /** Information about a feature that's no longer assigned to a site. */
  unassingedToFloating?: UnAssingedToFloatingReason;
  /**
   * Information about a feature that's been replaced by a feature from a
   * different subscription.
   */
  replacedByAnotherSubscription?: ReplacedByAnotherSubscriptionReason;
  /**
   * Information about a feature that's been reassigned to a different
   * site.
   */
  reassignedToAnotherSite?: ReassignedToAnotherSiteReason;
  /**
   * Disabled feature. Includes information about the feature's new state,
   * possibly its new context.
   */
  feature?: Feature;
  /**
   * ID of the meta site for which the feature has been disabled.
   * @format GUID
   */
  metaSiteId?: string | null;
}

/** @oneof */
export interface FeatureDisabledReasonOneOf {
  /** Information about a feature that's no longer assigned to a site. */
  unassingedToFloating?: UnAssingedToFloatingReason;
  /**
   * Information about a feature that's been replaced by a feature from a
   * different subscription.
   */
  replacedByAnotherSubscription?: ReplacedByAnotherSubscriptionReason;
  /**
   * Information about a feature that's been reassigned to a different
   * site.
   */
  reassignedToAnotherSite?: ReassignedToAnotherSiteReason;
}

/** Subscription was unassigned from the site and moved into floating state */
export interface UnAssingedToFloatingReason {}

/** Another subscription was assigned to the site, causing existing features on this site to be disabled */
export interface ReplacedByAnotherSubscriptionReason {}

/** Subscription was assigned to another site, causing  features on the origin  site to be disabled. */
export interface ReassignedToAnotherSiteReason {
  /**
   * Information about a transfer to the site.
   * @format GUID
   */
  reassignedToMetasite?: string;
}

/** Feature updated, for example Quota was increased due to upgrade */
export interface FeatureUpdated
  extends FeatureUpdatedPreviousQuantityInfoOneOf,
    FeatureUpdatedReasonOneOf {
  /** Information about a feature that doesn't have a usage quota. */
  booleanFeature?: BooleanFeature;
  /** Information about a feature that has a usage quota. */
  quotaFeature?: QuotaFeature;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /**
   * Updated feature. Includes information about the feature's new state and
   * possibly its new context.
   */
  feature?: Feature;
}

/** @oneof */
export interface FeatureUpdatedPreviousQuantityInfoOneOf {
  /** Information about a feature that doesn't have a usage quota. */
  booleanFeature?: BooleanFeature;
  /** Information about a feature that has a usage quota. */
  quotaFeature?: QuotaFeature;
}

/** @oneof */
export interface FeatureUpdatedReasonOneOf {
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
}

/** Feature was permanently cancelled */
export interface FeatureCancelled extends FeatureCancelledReasonOneOf {
  /** Information about a transfer to the account. */
  transferredToAnotherAccount?: TransferredToAnotherAccountReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the feature cancellation. */
  cancelRequest?: CancelRequestedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /** Feature was automatically cancelled because its quota was fully consumed. */
  quotaExhausted?: QuotaExhaustedReason;
  /** Canceled feature. */
  feature?: Feature;
  /**
   * Information about a transfer to the account.
   * __Deprecated__. Use `reason.transferred_to_account` instead.
   * @format GUID
   */
  transferredToAccount?: string | null;
}

/** @oneof */
export interface FeatureCancelledReasonOneOf {
  /** Information about a transfer to the account. */
  transferredToAnotherAccount?: TransferredToAnotherAccountReason;
  /** Information about the contract switch. */
  contractSwitched?: ContractSwitchedReason;
  /** Information about the feature cancellation. */
  cancelRequest?: CancelRequestedReason;
  /** Information about manual sync (e.g. immigrator task). */
  manualSync?: ManualSyncReason;
  /** Feature was automatically cancelled because its quota was fully consumed. */
  quotaExhausted?: QuotaExhaustedReason;
}

/** Subscription was transferred to another account, features in the origin account were cancelled */
export interface TransferredToAnotherAccountReason {
  /**
   * Information about a transfer to the account.
   * @format GUID
   */
  transferredToAccount?: string;
}

/** Cancellation was requested from the subscription manager api, might be a result of billing event, or direct call */
export interface CancelRequestedReason {}

/** Feature was automatically cancelled because its quota was fully consumed (e.g. topup feature) */
export interface QuotaExhaustedReason {}

/** Quota usage changed for a feature. */
export interface FeatureQuotaUsageChanged {
  /** Full feature state before this quota usage report. */
  featureBefore?: Feature;
  /** Full feature state after this quota usage report. */
  featureAfter?: Feature;
}

export interface EcomSitePropertiesChangedEvent {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type GetEcommerceSettingsApplicationErrors = {
  code?: 'SITE_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Retrieves site eCommerce settings.
 * @param fields - Fields to include in the response.
 * @public
 * @documentationMaturity preview
 * @requiredField fields
 * @permissionId ECOM.ECOMMERCE_SETTINGS_READ
 * @applicableIdentity APP
 * @fqn wix.ecom.ecommerce_settings.v1.EcommerceSettingsService.GetEcommerceSettings
 */
export async function getEcommerceSettings(
  fields: RequestedFieldsWithLiterals[]
): Promise<
  NonNullablePaths<
    GetEcommerceSettingsResponse,
    `errors` | `errors.${number}.requestedField`,
    4
  > & {
    __applicationErrorsType?: GetEcommerceSettingsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ fields: fields });

  const reqOpts =
    ambassadorWixEcomV1EcommerceSettings.getEcommerceSettings(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            {
              path: 'ecommerceSettings.checkout.checkoutBrand.header.logo.image',
            },
            {
              path: 'ecommerceSettings.checkout.checkoutBrand.header.backgroundFill.image',
            },
            {
              path: 'ecommerceSettings.checkout.checkoutBrand.mainSection.formBackgroundFill.image',
            },
            {
              path: 'ecommerceSettings.checkout.checkoutBrand.mainSection.summaryBackgroundFill.image',
            },
            {
              path: 'ecommerceSettings.checkout.checkoutBrand.footer.backgroundFill.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [{ path: 'ecommerceSettings.businessInfo.address' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { fields: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['fields']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
